import React, { useCallback, useContext, useEffect, useState } from 'react'

import { usePrevious } from 'react-use'

function getDocumentIsVisible() {
  return !document.hidden
}

type VisibilityContextType = { isVisible: boolean }

const VisibilityContext = React.createContext<VisibilityContextType>({
  isVisible: true,
})

export const VisibilityProvider: React.FC = ({ children }) => {
  const [isVisible, setIsVisible] = useState(getDocumentIsVisible())

  const handleVisibilityChange = useCallback(() => {
    const visible = getDocumentIsVisible()
    setIsVisible(visible)
  }, [])

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [handleVisibilityChange])

  return (
    <VisibilityContext.Provider value={{ isVisible }}>
      {children}
    </VisibilityContext.Provider>
  )
}

export function usePageVisibility(cb?: (visible: boolean) => void): boolean {
  const { isVisible } = useContext(VisibilityContext)
  const prevIsVisible = usePrevious(isVisible)

  useEffect(() => {
    if (prevIsVisible !== isVisible && typeof cb === 'function') {
      cb(isVisible)
    }
  }, [cb, isVisible, prevIsVisible])

  return isVisible
}
